import * as React from "react";
import { Pressable, Text, View } from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { cn } from "@/shared/lib/utils";

type TabItem<K extends string> = {
  key: K;
  label: string;
  icon?: React.ComponentProps<typeof Ionicons>["name"];
};

type UnderlineTabsProps<K extends string> = {
  items: readonly TabItem<K>[];
  activeKey: K;
  onChange: (key: K) => void;
  className?: string;
};

export function UnderlineTabs<K extends string>({
  items,
  activeKey,
  onChange,
  className,
}: UnderlineTabsProps<K>) {
  const [brand, mutedForeground] = useCSSVariable([
    "--color-brand",
    "--color-muted-foreground",
  ]) as [string, string];

  const activeIndex = items.findIndex((t) => t.key === activeKey);
  const [tabWidth, setTabWidth] = React.useState(0);

  const indicatorX = useSharedValue(activeIndex * tabWidth);

  React.useEffect(() => {
    indicatorX.value = withTiming(activeIndex * tabWidth, { duration: 200 });
  }, [activeIndex, tabWidth, indicatorX]);

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: indicatorX.value }],
  }));

  return (
    <View
      className={cn("border-border flex-row border-b", className)}
      onLayout={(e) => {
        const w = e.nativeEvent.layout.width / items.length;
        setTabWidth(w);
        indicatorX.value = activeIndex * w;
      }}
    >
      {items.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <Pressable
            key={tab.key}
            onPress={() => onChange(tab.key)}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            className="flex-1 flex-row items-center justify-center gap-2 pt-1 pb-3"
          >
            {tab.icon && (
              <Ionicons
                name={tab.icon}
                size={18}
                color={isActive ? brand : mutedForeground}
              />
            )}
            <Text
              className={cn(
                "text-base font-medium",
                isActive ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {tab.label}
            </Text>
          </Pressable>
        );
      })}

      {tabWidth > 0 && (
        <Animated.View
          style={[
            indicatorStyle,
            {
              position: "absolute",
              bottom: 0,
              left: 0,
              width: tabWidth,
              height: 2,
            },
          ]}
          className="bg-brand rounded-full"
        />
      )}
    </View>
  );
}
